// TYPE-ONLY on purpose: `NodeColumns` is a runtime enum in `table-store`, and
// importing it pulls the whole store (and, through it, the Lit design system)
// into this module — which breaks every node-env unit test that touches it.
// Same reason `group-row-guards.ts` imports `TableNode` as a type.
import type { TableNode } from '../table-store';

/**
 * Project Bobcat — the shared "what counts as Ungrouped, and how do we label
 * a row" vocabulary, used by the Build-Stage canvas (`canvas-view-model.ts`)
 * and, previously, Agent View's now-deleted read-only projection.
 *
 * Relocated here 2026-09-23 when Agent View was removed (canvas reached
 * feature parity with it — real-group rendering, the Ungrouped indicator —
 * and Elana confirmed the transitional page could go: "the agent view can go
 * away - the whole design is going to match mostafa's design and the 5 step
 * process"). These two exports were genuinely shared infrastructure living
 * under `pages/agent-view/`, not page-specific code, so they survive the
 * page's deletion; everything else in the old `agent-view-model.ts`
 * (`buildAgentView`, `buildProposedAgentView`, per-section node projection)
 * was Agent-View-grid-specific and was deleted along with the page.
 */

/** Shown when a node has neither a name nor a message to identify it by. */
export const UNLABELLED_NODE = '—';

/**
 * The id/label for the pseudo-section collecting nodes that belong to no
 * group — of ANY node type. A Use Case Agent is a REAL group (the same kind
 * of thing Supervisor Agent or Knowledge Agent is), so nothing with no group
 * at all is a use case agent; it is simply ungrouped, whatever its node type.
 */
export const UNGROUPED_SECTION_ID = '__ungrouped__';
export const UNGROUPED_SECTION_LABEL = 'Ungrouped';

/** Node name, else its message, else `UNLABELLED_NODE`. */
export const labelFor = (row: TableNode): string => {
  const { nodeName: name, message } = row;
  if (typeof name === 'string' && name.trim()) return name.trim();

  if (typeof message === 'string' && message.trim()) return message.trim();

  return UNLABELLED_NODE;
};
